import React, {useMemo, useState} from "react";
import SelectedItemBlock from "./selectedItemBlock";
import Loading from "../../../components/Common/Loading";
import {useGetChargesTypesV2Query} from "../../../core/api/charge-api";
import {useParams} from "react-router-dom";
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    FormControlLabel,
    FormGroup,
    Stack,
    Switch,
    Typography
} from "@mui/material";
import {useCreateChargeV2CMMutation} from "../../../core/api/clockmaker-charge-api";
import {useCmCheckExistMutation} from "../../../core/api/cloclmaker-state-api";
import {useGetAllChargeTemplatesQuery} from "../../../core/api/charge-template-api";
import {ChargeTemplate} from "../../Templates";
import ChargeTargetHint from "../chargeTargetHint";
import MassChargeWizard from "../massCharge/MassChargeWizard";
import {buildChargeDataCM, createCmAdapter, newCmEntry, updateCmItem} from "./chargeRequest";

const noop = () => {};

export default function CmChargeBlock(props) {

    const {game, deviceId: deviceIdParam} = useParams()

    const deviceId = deviceIdParam || ''

    const [checkExist] = useCmCheckExistMutation();
    const [createCharge, createChargeResponse] = useCreateChargeV2CMMutation()
    const {data: chargeTemplates} = useGetAllChargeTemplatesQuery(game)

    const {
        data: chargeTypes,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetChargesTypesV2Query(game)

    const [isMassCharge, setIsMassCharge] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [childChanged, setIsChildChanged] = useState(false);
    const [someChildIsBlocking, setSomeChildIsBlocking] = useState([])

    const handleAddItem = () => {
        props.setSelectedItemList([...props.selectedItemList, newCmEntry()])
    }

    const handleRemoveItem = (index) => {
        let newSomeChildBlocking = [...someChildIsBlocking];
        newSomeChildBlocking.splice(index, 1);
        setSomeChildIsBlocking(newSomeChildBlocking);
        let newSelectedItemList = [...props.selectedItemList];
        newSelectedItemList.splice(index, 1);
        props.setSelectedItemList(newSelectedItemList);
    }

    const handleChange = (event, item, fieldName) => {
        props.setSelectedItemList(props.selectedItemList.map(i =>
            i.id === item.id ? updateCmItem(item, fieldName, event, chargeTypes) : i
        ));
    }

    const handleChangeCustomFields = (propName, event, item) => {
        props.setSelectedItemList(props.selectedItemList.map(i =>
            i.id === item.id ? {...i, customFields: {...(i.customFields || {}), [propName]: event}} : i
        ));
    }

    const createChargeHandler = () => {
        setFormErrors({})
        if (deviceId === '') {
            setFormErrors({deviceId: "не указан State ID"})
            return false;
        }

        createCharge({
            "data": {
                "userIds": [deviceId],
                "chargeData": buildChargeDataCM(props.selectedItemList)
            },
            "version": 'v1'
        })
    }

    // ChargeTemplate passes the already parsed list
    const selectTemplate = (list) => {
        props.setSelectedItemList(list)
        setSomeChildIsBlocking(new Array(list.length).fill(false))
    }

    const toggleMassCharge = () => {
        setIsMassCharge(!isMassCharge)
    }

    const isChargeButtonDisable = someChildIsBlocking.includes(true)
    const idsToCharge = deviceId ? 1 : 0;
    const isChargeDisabled = isChargeButtonDisable || idsToCharge === 0;

    const massAdapter = useMemo(() => createCmAdapter(chargeTypes ?? []), [chargeTypes]);

    // The CM constructor for one item — used by the mass charge wizard as the item picker
    const renderItemEditor = (entry, onChange) => (
        <SelectedItemBlock
            isSuccess={isSuccess}
            item={entry}
            index={0}
            chargeTypes={chargeTypes}
            handleChange={(value, item, fieldName) => onChange(updateCmItem(item, fieldName, value, chargeTypes))}
            handleChangeCustomFields={(propName, value, item) =>
                onChange({...item, customFields: {...(item.customFields || {}), [propName]: value}})}
            handleRemoveItem={noop}
            setIsChildChanged={noop}
            childChanged={false}
            setSomeChildIsBlocking={noop}
            someChildIsBlocking={[]}
            selectedItemList={[entry]}
        />
    );

    return (
        <>
            {props.massCharge &&
                <FormGroup sx={{pb: 2}}>
                    <FormControlLabel
                        control={
                            <Switch checked={isMassCharge} onChange={toggleMassCharge} name="massCharge"/>
                        }
                        label="Mass charge"
                    />
                </FormGroup>}

            {isMassCharge && (
                <Loading isLoading={isLoading} isError={isError} error={error} isSuccess={isSuccess}>
                    {chargeTypes !== undefined && (
                        <MassChargeWizard
                            adapter={massAdapter}
                            templates={chargeTemplates ?? []}
                            buildChargeData={buildChargeDataCM}
                            checkExist={(ids) => checkExist({ids}).unwrap()}
                            createCharge={({userIds, chargeData}) =>
                                createCharge({data: {userIds, chargeData}, version: 'v1'}).unwrap()}
                            renderItemEditor={renderItemEditor}
                        />
                    )}
                </Loading>
            )}

            {!isMassCharge && <>
                <ChargeTargetHint
                    idsCount={idsToCharge}
                    isMassCharge={false}
                    onEnableMassCharge={props.massCharge ? toggleMassCharge : undefined}
                />

                <Loading
                    isLoading={isLoading}
                    isError={isError}
                    error={error}
                    isSuccess={isSuccess}>
                    {props.selectedItemList.map((item, index) => (
                        <SelectedItemBlock
                            key={item.id ?? index}
                            isSuccess={isSuccess}
                            item={item}
                            index={index}
                            chargeTypes={chargeTypes}
                            handleChange={handleChange}
                            handleChangeCustomFields={handleChangeCustomFields}
                            handleAddItem={handleAddItem}
                            handleRemoveItem={handleRemoveItem}
                            setIsChildChanged={setIsChildChanged}
                            childChanged={childChanged}
                            setSomeChildIsBlocking={setSomeChildIsBlocking}
                            someChildIsBlocking={someChildIsBlocking}
                            selectedItemList={props.selectedItemList}
                        />
                    ))}
                </Loading>

                {Object.keys(formErrors).length > 0 &&
                    <Alert severity="error" sx={{mb: 2}}>
                        {Object.values(formErrors).map(error => (
                            <div key={error}>{error}</div>
                        ))}
                    </Alert>
                }

                {createChargeResponse.isSuccess &&
                    <Alert severity="info" sx={{mb: 2}}>
                        Charge result done {createChargeResponse.data.userIds.length}:
                        {createChargeResponse.data.userIds.map(data => (
                            <div key={data}>{data}</div>
                        ))}
                    </Alert>
                }

                <Stack direction="row" spacing={2} sx={{alignItems: "center", mt: 1}}>
                    <LoadingButton
                        disabled={isChargeDisabled}
                        loading={createChargeResponse.isLoading}
                        variant={"contained"}
                        color={"success"}
                        onClick={createChargeHandler}>Charge</LoadingButton>
                    <Typography
                        variant="body2"
                        color={idsToCharge === 0 ? 'warning.main' : 'text.secondary'}
                    >
                        {idsToCharge} ids to charge
                    </Typography>
                </Stack>

                <ChargeTemplate
                    game={game}
                    isChargeButtonDisable={isChargeButtonDisable}
                    selectedItemList={props.selectedItemList}
                    setSelectedItemList={selectTemplate}/>
            </>}
        </>
    );
}
